import { StoryCard } from '../lib/types';
import Tag from './Tag';
import Soundwave from './Soundwave';
import { useEffect, useRef, useState } from 'react';

interface StoryCardItemProps {
  card: StoryCard;
  onClick?: () => void;
  variant?: 'default' | 'modal';
}

export default function StoryCardItem({ card, onClick, variant = 'default' }: StoryCardItemProps) {
  const allTags = [card.primaryIssueTag, ...(card.secondaryIssueTags || [])];
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleChips, setVisibleChips] = useState(allTags.length);
  const isModal = variant === 'modal';

  useEffect(() => {
    const measureChips = () => {
      if (!containerRef.current || !measureRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const chips = Array.from(measureRef.current.children) as HTMLElement[];

      let totalWidth = 0;
      let canFitAll = true;
      const gap = 6;

      for (let i = 0; i < chips.length; i++) {
        totalWidth += chips[i].offsetWidth;
        if (i > 0) totalWidth += gap;

        if (totalWidth > containerWidth) {
          canFitAll = false;
          break;
        }
      }

      if (canFitAll) {
        setVisibleChips(allTags.length);
      } else {
        setVisibleChips(2);
      }
    };

    measureChips();
    window.addEventListener('resize', measureChips);
    return () => window.removeEventListener('resize', measureChips);
  }, [allTags.length]);

  const hasOverflow = visibleChips < allTags.length;
  const overflowCount = allTags.length - visibleChips;

  return (
    <div
      className="bg-[#1A1A1A] rounded-2xl p-4 cursor-pointer hover:bg-[#1C1C1C] transition-all group relative border"
      style={{ borderColor: 'rgba(255, 255, 255, 0.08)', boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}
      onClick={onClick}
    >
      <div className="flex gap-3 md:gap-5">
        <div className="relative flex-shrink-0 self-start">
          <img
            src={card.albumArtUrl}
            alt={card.songTitle}
            className="w-[85px] h-[85px] md:w-28 md:h-28 lg:w-32 lg:h-32 rounded-lg object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder-civicbeat.svg';
            }}
          />
          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Soundwave variant="dots" color="#40E0D0" />
          </div>
        </div>
        <div className="flex-1 min-w-0 md:pl-2 flex flex-col justify-start md:justify-center">
          <div className="flex flex-col">
            <h3
              className={`font-semibold text-[#F5F5F5] truncate leading-tight ${
                isModal
                  ? 'text-[14px] md:text-[16px]'
                  : 'text-[15px] md:text-[18px]'
              }`}
              style={{ letterSpacing: '-0.005em' }}
            >
              {card.songTitle}
            </h3>
            <p
              className={`text-[#A0A0A0] truncate leading-tight ${
                isModal
                  ? 'text-[12px] md:text-[12.5px] mt-0.5'
                  : 'text-[13px] md:text-[13.5px] mt-0.5 md:mt-1.5'
              }`}
            >
              {card.artistName}
            </p>
          </div>
          <p
            className={`text-[#A0A0A0] line-clamp-2 ${
              isModal
                ? 'text-[12px] md:text-[13px] mt-1.5 md:mt-2'
                : 'text-[12.5px] md:text-[14px] mt-2 md:mt-4'
            }`}
            style={{ lineHeight: '1.55' }}
          >
            {card.hookText}
          </p>

          {/* Hidden measurement container */}
          <div
            ref={measureRef}
            className="flex gap-1.5 invisible absolute pointer-events-none md:hidden"
            aria-hidden="true"
          >
            {allTags.map((tag, index) => (
              <Tag key={`measure-${tag}-${index}`} variant="chip" small>
                {tag}
              </Tag>
            ))}
          </div>

          {/* Mobile: adaptive chips with overflow */}
          <div
            ref={containerRef}
            className={`flex gap-1.5 md:hidden overflow-hidden flex-nowrap ${
              isModal ? 'mt-2.5' : 'mt-2.5'
            }`}
          >
            {allTags.slice(0, visibleChips).map((tag, index) => (
              <Tag key={`${tag}-${index}`} variant="chip" small>
                {tag}
              </Tag>
            ))}
            {hasOverflow && (
              <Tag variant="chip" small>
                +{overflowCount}
              </Tag>
            )}
          </div>

          {/* Desktop: show all (up to 4) */}
          <div className={`hidden md:flex gap-1.5 overflow-hidden flex-nowrap ${
            isModal ? 'mt-3' : 'mt-4'
          }`}>
            <Tag variant="chip" small>
              {card.primaryIssueTag}
            </Tag>
            {card.secondaryIssueTags.slice(0, 3).map((tag, index) => (
              <Tag key={`${tag}-${index}`} variant="chip" small>
                {tag}
              </Tag>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
